import { buildAndFilter, buildOrFilter, groupRows } from '~/types';
import { textValueOf, type EditableFilterRow } from '~/types/filters';
import type { FilterConnector } from '~/types';
import { Currency, DeliveryMethod, LogisticsIncoterm } from '~/graphql/graphql';
import type {
  NumberOperatorInput,
  OrderFiltersInput,
  StringOperatorInput,
  TradePerspective,
} from '~/graphql/graphql';

export type OrderFilterConnector = FilterConnector;

export type OrderFilterRow = EditableFilterRow;

/**
 * Rows are removed from the middle of the list, so React needs an identity that
 * survives the reindex. A random id rather than a counter, because a saved view
 * rehydrates rows a later counter would collide with.
 */
export function newOrderFilterRowId(): string {
  return crypto.randomUUID();
}

function namesAnyValue(value: string): boolean {
  return value.replace(/[\s,]/g, '') !== '';
}

/**
 * Local-calendar day window. `new Date(y, m, d + 1)` is the next local midnight;
 * adding 24 hours is not, because a DST transition makes the day 23 or 25 hours
 * long and would slide the window off the date the user picked.
 */
export function dayBounds(isoDate: string): {
  dayStart: string;
  dayEnd: string;
} {
  const [year, month, day] = isoDate.split('-').map(Number);
  return {
    dayStart: new Date(year, month - 1, day).toISOString(),
    dayEnd: new Date(year, month - 1, day + 1).toISOString(),
  };
}

export const ORDER_FILTER_FIELD = {
  purchaseOrderNumber: 'purchaseOrderNumber',
  counterparty: 'counterparty',
  product: 'product',
  plant: 'plant',
  finalDeliveryLocation: 'finalDeliveryLocation',
  orderDate: 'orderDate',
  shipDate: 'shipDate',
  deliveryWindowStart: 'deliveryWindowStart',
  deliveryWindowEnd: 'deliveryWindowEnd',
  total: 'total',
  itemCount: 'itemCount',
  incoterm: 'incoterm',
  deliveryMethod: 'deliveryMethod',
  currency: 'currency',
  createdBy: 'createdBy',
} as const;

export type OrderFilterField =
  (typeof ORDER_FILTER_FIELD)[keyof typeof ORDER_FILTER_FIELD];

export type DateOperator =
  'is' | 'isNot' | 'before' | 'after' | 'onOrBefore' | 'onOrAfter';

const TEXT_FIELDS = [
  ORDER_FILTER_FIELD.purchaseOrderNumber,
  ORDER_FILTER_FIELD.counterparty,
  ORDER_FILTER_FIELD.product,
  ORDER_FILTER_FIELD.plant,
  ORDER_FILTER_FIELD.finalDeliveryLocation,
] as const;

const DATE_FIELDS = [
  ORDER_FILTER_FIELD.orderDate,
  ORDER_FILTER_FIELD.shipDate,
  ORDER_FILTER_FIELD.deliveryWindowStart,
  ORDER_FILTER_FIELD.deliveryWindowEnd,
] as const;

const NUMBER_FIELDS = [
  ORDER_FILTER_FIELD.total,
  ORDER_FILTER_FIELD.itemCount,
] as const;

const ENUM_FIELDS = [
  ORDER_FILTER_FIELD.incoterm,
  ORDER_FILTER_FIELD.deliveryMethod,
  ORDER_FILTER_FIELD.currency,
] as const;

const ID_FIELDS = [ORDER_FILTER_FIELD.createdBy] as const;

const TEXT_OPERATORS = [
  'contains',
  'endsWith',
  'eq',
  'neq',
  'notContain',
  'startsWith',
] as const;

const NUMBER_OPERATORS = ['eq', 'gt', 'gte', 'lt', 'lte', 'neq'] as const;

const ENUM_OPERATORS = ['eq', 'neq'] as const;

const ID_OPERATORS = ['eq'] as const;

const DATE_OPERATORS: readonly DateOperator[] = [
  'is',
  'isNot',
  'before',
  'after',
  'onOrBefore',
  'onOrAfter',
];

function isOneOf<T extends string>(
  value: string,
  values: readonly T[]
): value is T {
  return (values as readonly string[]).includes(value);
}

const ENUM_VALUES: Record<string, readonly string[]> = {
  [ORDER_FILTER_FIELD.incoterm]: Object.values(LogisticsIncoterm),
  [ORDER_FILTER_FIELD.deliveryMethod]: Object.values(DeliveryMethod),
  [ORDER_FILTER_FIELD.currency]: Object.values(Currency),
};

export function operatorsForField(field: string): readonly string[] | null {
  if (isOneOf(field, TEXT_FIELDS)) return TEXT_OPERATORS;
  if (isOneOf(field, DATE_FIELDS)) return DATE_OPERATORS;
  if (isOneOf(field, NUMBER_FIELDS)) return NUMBER_OPERATORS;
  if (isOneOf(field, ENUM_FIELDS)) return ENUM_OPERATORS;
  if (isOneOf(field, ID_FIELDS)) return ID_OPERATORS;
  return null;
}

/** The counterparty row is one field in the UI and two in the schema. */
function textFieldKey(
  field: string,
  perspective: TradePerspective
): keyof OrderFiltersInput {
  if (field === ORDER_FILTER_FIELD.counterparty) {
    return perspective === 'BUYING'
      ? 'sellerOrganization'
      : 'buyerOrganization';
  }
  return field as keyof OrderFiltersInput;
}

function textClause(
  key: keyof OrderFiltersInput,
  operator: string,
  part: string
): OrderFiltersInput {
  const input: StringOperatorInput = { [operator]: part };
  return { [key]: input } as OrderFiltersInput;
}

function numberClause(
  field: string,
  operator: string,
  value: number
): OrderFiltersInput {
  const input: NumberOperatorInput = { [operator]: value };
  return { [field]: input } as OrderFiltersInput;
}

function idClause(field: string, value: string): OrderFiltersInput {
  return { [field]: value } as OrderFiltersInput;
}

function enumClause(
  field: string,
  operator: string,
  value: string
): OrderFiltersInput {
  const input = { [operator]: value } as
    | { eq?: LogisticsIncoterm | DeliveryMethod | Currency }
    | { neq?: LogisticsIncoterm | DeliveryMethod | Currency };
  return { [field]: input } as OrderFiltersInput;
}

function isCalendarDate(value: string): boolean {
  const parts = value.split('-');
  if (parts.length !== 3) {
    return false;
  }
  const [year, month, day] = parts.map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function dateClause(
  field: string,
  operator: string,
  value: string
): OrderFiltersInput | null {
  if (!isCalendarDate(value)) {
    return null;
  }
  const { dayStart, dayEnd } = dayBounds(value);
  const at = (bound: 'gte' | 'lt', instant: string): OrderFiltersInput =>
    ({ [field]: { [bound]: instant } }) as OrderFiltersInput;

  switch (operator as DateOperator) {
    case 'is':
      return { and: [at('gte', dayStart), at('lt', dayEnd)] };
    case 'isNot':
      return { or: [at('lt', dayStart), at('gte', dayEnd)] };
    case 'before':
      return at('lt', dayStart);
    case 'after':
      return at('gte', dayEnd);
    case 'onOrBefore':
      return at('lt', dayEnd);
    case 'onOrAfter':
      return at('gte', dayStart);
    default:
      return null;
  }
}

export function rowToClause(
  row: OrderFilterRow,
  perspective: TradePerspective
): OrderFiltersInput | null {
  const value = textValueOf(row).trim();
  if (value === '') {
    return null;
  }

  if (isOneOf(row.field, TEXT_FIELDS)) {
    if (!isOneOf(row.operator, TEXT_OPERATORS)) {
      return null;
    }
    if (!namesAnyValue(value)) {
      return null;
    }
    return textClause(
      textFieldKey(row.field, perspective),
      row.operator,
      value
    );
  }

  if (isOneOf(row.field, DATE_FIELDS)) {
    return dateClause(row.field, row.operator, value);
  }

  if (isOneOf(row.field, NUMBER_FIELDS)) {
    if (!isOneOf(row.operator, NUMBER_OPERATORS)) {
      return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    // The backend rejects a fractional, negative, or out-of-long-range item
    // count with a bad request, so such a row is dropped here rather than sent
    // and failed. isSafeInteger covers the range check: anything beyond it
    // cannot survive longValueExact() server-side either.
    if (
      row.field === ORDER_FILTER_FIELD.itemCount &&
      (!Number.isSafeInteger(parsed) || parsed < 0)
    ) {
      return null;
    }
    return numberClause(row.field, row.operator, parsed);
  }

  if (isOneOf(row.field, ENUM_FIELDS)) {
    if (!isOneOf(row.operator, ENUM_OPERATORS)) {
      return null;
    }
    if (!isOneOf(value, ENUM_VALUES[row.field])) {
      return null;
    }
    return enumClause(row.field, row.operator, value);
  }

  if (isOneOf(row.field, ID_FIELDS)) {
    if (!isOneOf(row.operator, ID_OPERATORS)) {
      return null;
    }
    return idClause('buyerId', value);
  }

  return null;
}

export type ActiveFilterRow = {
  row: OrderFilterRow;
  clause: OrderFiltersInput;
};

export function activeFilterGroups(
  rows: OrderFilterRow[],
  perspective: TradePerspective
): ActiveFilterRow[][] {
  const groups: ActiveFilterRow[][] = [];
  for (const group of groupRows(rows)) {
    const active: ActiveFilterRow[] = [];
    for (const row of group) {
      const clause = rowToClause(row, perspective);
      if (clause !== null) {
        active.push({ row, clause });
      }
    }
    if (active.length > 0) {
      groups.push(active);
    }
  }
  return groups;
}

export function buildOrderFiltersInput(
  rows: OrderFilterRow[],
  perspective: TradePerspective
): OrderFiltersInput {
  const groups = activeFilterGroups(rows, perspective);

  if (groups.length === 0) {
    return {};
  }

  const groupInputs = groups.map((group) => {
    const clauses = group.map((active) => active.clause);
    return clauses.length === 1
      ? clauses[0]
      : (buildAndFilter(clauses) as OrderFiltersInput);
  });

  return groupInputs.length === 1
    ? groupInputs[0]
    : (buildOrFilter(groupInputs) as OrderFiltersInput);
}

export function activeRowCount(
  rows: OrderFilterRow[],
  perspective: TradePerspective
): number {
  return activeFilterGroups(rows, perspective).flat().length;
}
